import { ArrowsLeftRightIcon, CheckCircleIcon, HouseIcon, SpinnerGapIcon } from '@phosphor-icons/react'
import { useState } from 'react'

import { HojaInferior } from '@/components/HojaInferior'
import { ApiError, household } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { useSession } from '@/lib/session'

const TIPO_HOGAR: Record<string, string> = {
  PERSONAL: 'Personal',
  COUPLE: 'Pareja',
  GROUP: 'Familia',
}

/**
 * Cambiar de hogar.
 *
 * Pertenecer a varios es normal: quien ya usaba PayBae por su cuenta y después
 * entra al de su pareja queda en los dos. El servidor guarda cuál está mirando
 * cada quien; esto es lo que permite decirlo.
 *
 * El cambio **recarga la app**. Lo que hay en memoria son saldos, movimientos y
 * tarjetas del hogar anterior, y enseñarlos un instante bajo el nombre del
 * nuevo sería enseñar el dinero de otra casa.
 */
export function ListaHogares() {
  const { profile, membership } = useSession()
  const [cambiando, setCambiando] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const cambiar = async (householdId: string): Promise<void> => {
    setCambiando(householdId)
    setError(null)
    try {
      await household.switch(householdId)
      haptic('success')
      // Recarga completa, no invalidación.
      //
      // Cambiar de hogar cambia *todos* los números de la pantalla: saldos,
      // movimientos, tarjetas, metas, integrantes y hasta si toca el asistente
      // de configuración. Vaciar la caché y reconsultar deja media docena de
      // pantallas montadas con datos de la casa anterior mientras llegan los
      // nuevos, y basta que una consulta no se rehaga para enseñar el dinero de
      // otra gente.
      //
      // Además, `queryClient.clear()` **borra** la consulta de sesión, así que
      // el `invalidateQueries` que venía después no encontraba nada que
      // refrescar y la app se quedaba mirando el hogar viejo.
      //
      // Es una acción rara y deliberada; un arranque en frío cuesta menos que
      // un estado a medias.
      window.location.assign('/')
    } catch (cause) {
      haptic('error')
      setError(cause instanceof ApiError ? cause.message : 'No pudimos cambiarte de hogar.')
      setCambiando(null)
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      {profile.memberships.map((m) => {
        const activo = m.id === membership.id
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => {
              if (activo) return
              haptic('light')
              void cambiar(m.household.id)
            }}
            aria-current={activo}
            disabled={cambiando !== null}
            className={cn(
              'flex min-h-[56px] items-center gap-3 rounded-card px-3 text-left transition-colors',
              activo ? 'bg-primary-soft' : 'active:bg-surface-sunken disabled:opacity-60',
            )}
          >
            <span
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-full',
                activo ? 'bg-accion text-on-accion' : 'bg-surface-sunken text-ink-faint',
              )}
            >
              <HouseIcon size={17} weight={activo ? 'fill' : 'regular'} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-semibold text-ink">
                {m.household.name}
              </span>
              <span className="block text-[12px] text-ink-soft">
                {TIPO_HOGAR[m.household.kind] ?? m.household.kind}
                {activo ? ' · lo estás viendo' : ''}
              </span>
            </span>
            {cambiando === m.household.id ? (
              <SpinnerGapIcon size={17} className="shrink-0 animate-spin text-ink-faint" />
            ) : activo ? (
              <CheckCircleIcon size={19} weight="fill" className="shrink-0 text-primary-text" />
            ) : null}
          </button>
        )
      })}
      {error ? (
        <p role="alert" className="px-3 pb-1 text-[12px] text-expense-text">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/**
 * El mismo selector, flotando sobre una pantalla que no deja salir.
 *
 * Existe por un callejón sin salida real: aceptar una invitación deja activo el
 * hogar nuevo, y un hogar recién estrenado no tiene ingresos, así que el
 * asistente de configuración se pone delante de todo. Sin esto, quien ya tenía
 * su propio hogar entra al de su pareja, cae en el asistente y **no puede
 * volver al suyo** —Ajustes está detrás del asistente— salvo cerrando sesión.
 *
 * No se dibuja con un solo hogar: ahí no hay nada entre lo que elegir.
 */
export function BotonCambiarHogar() {
  const { profile } = useSession()
  const [abierta, setAbierta] = useState(false)

  if (profile.memberships.length < 2) return null

  return (
    <>
      <button
        type="button"
        onClick={() => {
          haptic('light')
          setAbierta(true)
        }}
        className={cn(
          // El área segura va en la POSICIÓN, no en el relleno. Antes era
          // `pt-safe` sobre la propia pastilla: en el iPhone instalado eso le
          // sumaba ~47 px de relleno por arriba y la pastilla crecía hacia
          // abajo con el texto pegado al fondo. Esta cuenta la deja a la altura
          // de la fila «Paso 1 de 4» del asistente, que es sobre lo que flota.
          'fixed right-3 top-[calc(max(env(safe-area-inset-top),0.5rem)_+_0.5rem)] z-50',
          // Mantequilla y no blanco con sombra: flota sobre el bloque de tinta
          // del asistente y, al bajar, sobre el papel, y es el único color que
          // se separa de los dos sin necesitar sombra. Es además la única
          // salida de una pantalla que no deja salir, así que gastar en ella el
          // acento de marca es justo.
          'flex min-h-[40px] items-center gap-1.5 rounded-pill bg-brand px-3.5',
          'text-[13px] font-bold text-on-brand active:scale-95',
        )}
      >
        <ArrowsLeftRightIcon size={15} weight="bold" />
        Cambiar de hogar
      </button>

      <HojaInferior
        abierta={abierta}
        titulo="Tus hogares"
        descripcion="Elige cuál quieres ver. Puedes volver cuando quieras."
        onCerrar={() => setAbierta(false)}
      >
        <ListaHogares />
      </HojaInferior>
    </>
  )
}
